import React, { useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { INTERAX_COLORS } from '../../../../constants/colors'
import type { PersonalityTraitPair } from '../helpers/myProfileHelpers'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'

const MotionBox = motion(Box)

export interface PersonalityTraitsVisualizationProps {
  /**
   * Array of personality trait pairs with values (0-100)
   */
  traits: PersonalityTraitPair[]
  /**
   * Optional title for the visualization
   */
  title?: string
}

const SPRING_CONFIG = {
  stiffness: 160,
  damping: 24,
  mass: 0.6
}

const LIGHT_SIZE = 520

/**
 * PersonalityTraitsVisualization - Interactive slider visualization for personality traits
 *
 * Features:
 * - Glassmorphism card styling
 * - Interactive spotlight effect following mouse
 * - Slider bars for each trait pair
 * - Smooth animations using framer-motion
 * - Orange gradient accents
 */
export const PersonalityTraitsVisualization: React.FC<PersonalityTraitsVisualizationProps> = ({ traits, title }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  // Mouse position tracking for spotlight effect
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const smoothX = useSpring(mouseX, SPRING_CONFIG)
  const smoothY = useSpring(mouseY, SPRING_CONFIG)

  const offsetX = useTransform(smoothX, (value) => value - LIGHT_SIZE / 2)
  const offsetY = useTransform(smoothY, (value) => value - LIGHT_SIZE / 2)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    mouseX.set(e.clientX - rect.left)
    mouseY.set(e.clientY - rect.top)
  }

  const handleMouseLeave = () => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    mouseX.set(rect.width / 2)
    mouseY.set(rect.height / 2)
  }

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '346px',
        // Glassmorphism card styling
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(6px)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        overflow: 'hidden'
      }}
    >
      {/* Spotlight effect - radial gradient following mouse */}
      <MotionBox
        style={{
          x: offsetX,
          y: offsetY
        }}
        sx={{
          position: 'absolute',
          width: `${LIGHT_SIZE}px`,
          height: `${LIGHT_SIZE}px`,
          background: `radial-gradient(circle, ${INTERAX_COLORS.sun40} 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {title && (
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '28px',
              color: INTERAX_COLORS.white,
              margin: 0
            }}
          >
            {title}
          </Typography>
        )}

        {/* Trait sliders */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {traits.map((trait) => (
            <TraitSlider key={trait.id} trait={trait} />
          ))}
        </Box>
      </Box>

      {/* Orange blur decorative element at bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: `linear-gradient(to top, ${INTERAX_COLORS.sun20} 0%, transparent 100%)`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
    </Box>
  )
}

interface TraitSliderProps {
  trait: PersonalityTraitPair
}

/**
 * Individual trait slider bar component
 * Supports two layouts:
 * - Personality traits: labels on sides, trait pairs
 * - Skill-based traits: label on top, "Poor" and "Strong" below the bar
 */
const TraitSlider: React.FC<TraitSliderProps> = ({ trait }) => {
  const sliderRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { left, right, value, id } = trait

  // Check if this is a skill-based trait
  const isSkillBased = id === 'communication' || id === 'helpingAssisting' || id === 'decisionMaking'

  // Calculate position (0-100 maps to slider position)
  const positionPercent = value

  // Get skill label for skill-based traits
  const getSkillLabel = () => {
    switch (id) {
      case 'communication':
        return t('gamer.collaborationProfile.personalityTraits.communicationSkills', {
          defaultValue: 'Communication skills'
        })
      case 'helpingAssisting':
        return t('gamer.collaborationProfile.personalityTraits.helpingAssistingSkills', {
          defaultValue: 'Helping and assisting skills'
        })
      case 'decisionMaking':
        return t('gamer.collaborationProfile.personalityTraits.decisionMakingSkills', {
          defaultValue: 'Decision making skills'
        })
      default:
        return ''
    }
  }

  if (isSkillBased) {
    // Skill-based trait layout: label on top, bar, "Poor" and "Strong" below
    // Label: 20px line height + 4px marginBottom = 24px
    // Gap: 8px
    // Track starts at: 24px + 8px = 32px
    // Track center: 32px + 6px = 38px
    // Knob top: 38px - 5px = 33px
    return (
      <Box
        ref={sliderRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          position: 'relative',
          paddingBottom: '4px'
        }}
      >
        {/* Skill label on top */}
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: INTERAX_COLORS.white,
            marginBottom: '4px'
          }}
        >
          {getSkillLabel()}
        </Typography>

        {/* Slider track container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '12px'
          }}
        >
          {/* Slider track */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '12px',
              background: INTERAX_COLORS.offBlack,
              borderRadius: '10px',
              boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
              isolation: 'isolate'
            }}
          />

          {/* Knob - positioned on track center */}
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${positionPercent}% - 5px)`,
              top: '1px', // Center of 12px track: (12px - 10px) / 2 = 1px
              boxSizing: 'border-box',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
              border: '0.14px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0px 0px 1.41px rgba(0, 0, 0, 0.5), 0px 0px 2.82px rgba(255, 171, 64, 0.4)',
              zIndex: 2
            }}
          />
        </Box>

        {/* Poor and Strong labels below the bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2px',
            paddingTop: '2px'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: INTERAX_COLORS.white,
              opacity: 0.8
            }}
          >
            {left}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: INTERAX_COLORS.white,
              opacity: 0.8
            }}
          >
            {right}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Personality trait layout: labels on sides
  return (
    <Box
      ref={sliderRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        position: 'relative',
        paddingBottom: '6px'
      }}
    >
      {/* Labels row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: INTERAX_COLORS.white,
            flex: 1,
            textAlign: 'left'
          }}
        >
          {left}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: INTERAX_COLORS.white,
            flex: 1,
            textAlign: 'right'
          }}
        >
          {right}
        </Typography>
      </Box>

      {/* Slider track - empty track only, no fill gradient, with depth */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '12px',
          background: INTERAX_COLORS.offBlack,
          borderRadius: '10px',
          boxShadow: 'inset 2px 2px 3px rgba(0, 0, 0, 0.5)',
          isolation: 'isolate'
        }}
      />

      {/* Knob - positioned outside track container, centered on track */}
      {/* Calculation: labels (20px) + gap (10px) + track center (6px) - half knob (5px) = 31px */}
      <Box
        sx={{
          position: 'absolute',
          left: `calc(${positionPercent}% - 5px)`,
          top: '31px',
          boxSizing: 'border-box',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: 'linear-gradient(360deg, #FFD196 0%, #FFAB40 23.08%, #FFFFFF 100%)',
          border: '0.14px solid rgba(255, 255, 255, 0.25)',
          boxShadow: '0px 0px 1.41px rgba(0, 0, 0, 0.5), 0px 0px 2.82px rgba(255, 171, 64, 0.4)',
          zIndex: 2
        }}
      />
    </Box>
  )
}

export default PersonalityTraitsVisualization
